class AccountBillingController {
  constructor($window, $scope, BillingService) {
    'ngInject';

    this.$window = $window;
    this.$scope = $scope;
    this.BillingService = BillingService;

    this.planInfo = {
      session_url: null
    };

    this.stripe = this.$window.Stripe('pk_test_your_publishable_key');
    this.elements = this.stripe.elements();
    this.card = null;
    this.subscription = {};
    this.usage = {
      activeProperties: 0,
      projects: 0,
      contacts: 0,
      users: 0,
      storage: 0
    };
    this.limits = {
      free: this.BillingService.FREE_PLAN_LIMITS,
      pro: this.BillingService.PRO_PLAN_LIMITS
    };
    this.exceededLimits = {};
    this.invoices = [];
    this.isLoading = false;
    this.error = null;

    this.initialize();
  }

  async initialize() {
    try {
      // Check if we have a successful payment
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      if (sessionId) {
        const session = await this.stripe.checkout.sessions.retrieve(sessionId);
        if (session.payment_status === 'paid') {
          this.subscription.plan = 'pro';
        }
      }

      // Fetch current subscription status
      const subscriptionResponse = await this.BillingService.getSubscription();
      this.subscription = subscriptionResponse.data;

      // Get current usage
      const usageResponse = await this.BillingService.getUsage();
      this.usage = usageResponse.data;

      // Check if any free plan limits are exceeded
      this.exceededLimits = this.BillingService.checkFreePlanLimits(this.usage);

      // Get billing history
      if (this.subscription.plan === 'pro') {
        const invoicesResponse = await this.BillingService.getInvoices();
        this.invoices = invoicesResponse.data;
      }

      // Setup Stripe Elements for pro users
      if (this.subscription.plan === 'pro') {
        this.setupStripeElements();
      }

      this.$scope.$apply();
    } catch (error) {
      this.error = error.message;
      this.$scope.$apply();
    }
  }

  setupStripeElements() {
    // Create card element
    const style = {
      base: {
        color: '#32325d',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#aab7c4'
        }
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a'
      }
    };

    this.card = this.elements.create('card', { style });
    this.card.mount('#card-element');

    // Handle real-time validation errors
    this.card.addEventListener('change', (event) => {
      const displayError = document.getElementById('card-errors');
      if (event.error) {
        displayError.textContent = event.error.message;
      } else {
        displayError.textContent = '';
      }
    });
  }

  getUsagePercentage(feature) {
    const limit = this.subscription.plan === 'pro'
      ? this.limits.pro[feature]
      : this.limits.free[feature];

    // If limit is -1 (unlimited), return 0
    if (limit === -1) return 0;

    return (this.usage[feature] / limit) * 100;
  }

  isUnlimited(feature) {
    return this.subscription.plan === 'pro' && this.limits.pro[feature] === -1;
  }

  getLimitText(feature) {
    const limit = this.subscription.plan === 'pro'
      ? this.limits.pro[feature]
      : this.limits.free[feature];

    return limit === -1 ? 'Unlimited' : limit;
  }

  async handlePaymentMethodSubmit() {
    try {
      this.isLoading = true;
      this.error = null;

      const { paymentMethod, error } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.card
      });

      if (error) {
        throw error;
      }

      await this.BillingService.updatePaymentMethod(paymentMethod.id);

      // Reset the form
      this.card.clear();
      this.isLoading = false;
      this.$scope.$apply();
    } catch (error) {
      this.error = error.message;
      this.isLoading = false;
      this.$scope.$apply();
    }
  }

  async upgradeToPro() {
    try {
      this.isLoading = true;
      this.error = null;

      const response = await this.BillingService.createCheckoutSession(
        this.BillingService.PRICE_IDS.PRO_PLAN
      );

      // Redirect to Stripe Checkout
      const result = await this.stripe.redirectToCheckout({
        sessionId: response.data.url
      });

      if (result.error) {
        throw result.error;
      }
    } catch (error) {
      this.error = error.message;
      this.isLoading = false;
      this.$scope.$apply();
    }
  }

  async enableWhiteLabel() {
    try {
      this.isLoading = true;
      this.error = null;

      const response = await this.BillingService.enableWhiteLabel();

      // Redirect to Stripe Checkout
      const result = await this.stripe.redirectToCheckout({
        sessionId: response.data.url
      });

      if (result.error) {
        throw result.error;
      }
    } catch (error) {
      this.error = error.message;
      this.isLoading = false;
      this.$scope.$apply();
    }
  }

  calculateAdditionalPropertiesCost() {
    if (this.usage.activeProperties <= this.limits.pro.activeProperties) return 0;
    return (this.usage.activeProperties - this.limits.pro.activeProperties) * 0.8;
  }

  shouldUpgrade() {
    return Object.values(this.exceededLimits).some(exceeded => exceeded);
  }
}

export default AccountBillingController;
